import { CommonModule } from '@angular/common';
import { Component, computed, inject, Signal } from '@angular/core';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { NoteBlockComponent } from '../note-block/note-block.component';
import { DeletedEntity } from '@app/_interfaces/feature.interface';
import { createEntry } from '@app/_models/entry.model';
import { NoteStore, ViewSettingsStore } from '@app/_stores';
import { Note } from '@app/_interfaces/note.interface';
import { SettingsService } from '@app/_services/settings.service';
import { AlertService } from '@app/_services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { CacheService } from '@app/_services/cache.service';
import { DependencyService } from '@app/_services/dependency.service';
import { sortByLocalDate } from '@app/_services/date';
import { ModalService } from '@app/_services/modal.service';

@Component({
  selector: 'app-note-trash-tab',
  imports: [
    CommonModule,
    SvgComponent,
    NoteBlockComponent
  ],
  templateUrl: './note-trash-tab.component.html',
  styleUrl: './note-trash-tab.component.scss'
})

export class NoteTrashTabComponent {

  readonly noteStore = inject(NoteStore);
  // Show ALL metadata in trash screen (description is controlled by descriptionType, not show array)
  show: string[] = ['label', 'time', 'attachments', 'mood', 'collaboratorsCount', 'access', 'hashtag', 'deletedAt'];
  viewSettingsStore = inject(ViewSettingsStore)

  deletedNotes: Signal<DeletedEntity[]> = computed(() => {
    const deletedEntities: DeletedEntity[] = [];
    this.noteStore.notes().forEach(note => {
      if (note.deletedAt && !note.permaDeletedAt && note.uid === this.cc.user.uid) {
        deletedEntities.push({
          entityName: 'note',
          entityId: note.id,
          deletedAt: note.deletedAt,
          entries: [],
          entityEntry: createEntry({
            setupId: note.id,
            entityType: 'note',
            date: '',
            occurDate: '',
            sortTitle: note.title,
            isScheduled: false,
            sortCreatedAt: note.createdAt ?? null,
            tagIds: note.tags || [],
          }),
          isRepeated: false
        });
      }
    });

    // Use timezone-aware sorting by deletedAt (newest first)
    return sortByLocalDate(
      deletedEntities,
      (entity) => entity.deletedAt,
      'desc'
    );
  });

  idToNote: Signal<Record<string, Note>> = this.noteStore.idToNote;

  constructor(
    public ss: SettingsService,
    private alertService: AlertService,
    public dialog: MatDialog,
    public cc: CacheService,
    public ds: DependencyService,
    private modalService: ModalService
  ) { }

  note(entityId: string): Note {
    return this.noteStore.idToNote()[entityId];
  };

  trackByEntity(index: number, entity: DeletedEntity) {
    return entity.entityId;
  };

  async remove(entityId: string) {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('removeConfirmation_title'),
      description: this.ss.t('removeConfirmation_content'),
      yesButtonText: this.ss.t('common_buttonRemove'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const note = this.note(entityId);
    note.permaDeletedAt = new Date();
    this.noteStore.updateNotes([note]);
  };

  async restore(entityId: string) {

    const isNoteEnabled = this.viewSettingsStore.showNoteFeature();
    if (!isNoteEnabled) {
      await this.alertService.alert(this.ss.t('featureTurnedOff_title'), this.ss.t('featureTurnedOff_content'), this.ss.t('common_ok'));
    }

    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('restoreConfirmation_title'),
      description: this.ss.t('restoreConfirmation_content'),
      yesButtonText: this.ss.t('common_restore'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const note = this.note(entityId);
    note.deletedAt = null;
    this.noteStore.updateNotes([note]);
    const isShow = await this.alertService.toast('noteRestored_content', isNoteEnabled ? 'common_show' : '', 'common_dismiss');
    if (isShow && isNoteEnabled) {
      const { NoteFormComponent } = await import('../note-form/note-form.component');
      this.modalService.openLevel1(
        NoteFormComponent,
        {
          mode: 'edit',
          value: note,
        },
        { disableClose: true, autoFocus: false }
      );
    }
  }
}
